import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Clock, Play, Square } from 'lucide-react';
import {
  type SessionRecording,
  useSessionRecordings,
} from '@/hooks/useSessionRecordings';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';
import SessionRecordingPlayer from './SessionRecordingPlayer';

interface SessionRecordingsProps {
  deviceId: string;
}

const TYPE_LABEL: Record<string, string> = {
  provisioning: 'Provisioning',
  proxy: 'Interactive',
  config: 'Config change',
};

const TYPE_STYLE: Record<string, string> = {
  provisioning: 'bg-sev-5/15 text-sev-5',
  proxy: 'bg-brand-soft text-brand',
  config: 'bg-sev-3/20 text-foreground',
};

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${String(Math.round(seconds))}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) return `${String(mins)}m ${String(secs)}s`;
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${String(hours)}h ${String(remainMins)}m`;
}

const PAGE_SIZE = 25;

/**
 * SessionRecordings — list of asciicast v2 recordings for a device.
 * Embedded in DeviceDetails > Recordings tab.
 *
 * Click Play → expand the asciicast player inline below the card.
 * Only one recording plays at a time; Play again to close.
 *
 * Paginated server-side: devices accumulate thousands of recordings
 * (every provisioning/config session is captured), and rendering them
 * all in one response is what used to make this tab crawl.
 */
export default function SessionRecordings({ deviceId }: SessionRecordingsProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useSessionRecordings(deviceId, page, PAGE_SIZE);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const recordings = data?.recordings;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load session recordings.</AlertDescription>
      </Alert>
    );
  }

  if (!recordings || recordings.length === 0) {
    return (
      <EmptyState
        variant="no-data"
        title="No recordings yet"
        description="Sessions on this device will be captured here automatically."
        size="inline"
      />
    );
  }

  const handlePlay = (recording: SessionRecording) => {
    setPlayingId(playingId === recording.id ? null : recording.id);
  };

  const goToPage = (next: number) => {
    setPlayingId(null);
    setPage(next);
  };

  return (
    <div className="space-y-3">
      {recordings.map((recording) => {
        const isPlaying = playingId === recording.id;
        return (
          <div
            key={recording.id}
            className="bg-card border border-border rounded-lg p-4"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  className={cn(
                    'border-0 font-normal',
                    TYPE_STYLE[recording.sessionType] ?? 'bg-muted text-muted-foreground',
                  )}
                >
                  {TYPE_LABEL[recording.sessionType] ?? recording.sessionType}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(recording.startedAt), { addSuffix: true })}
                </span>
                {recording.durationSeconds !== null ? (
                  <Badge variant="secondary" className="font-normal">
                    <Clock className="w-3 h-3" />
                    {formatDuration(recording.durationSeconds)}
                  </Badge>
                ) : null}
              </div>
              <Button
                variant={isPlaying ? 'default' : 'outline'}
                size="sm"
                onClick={() => { handlePlay(recording); }}
              >
                {isPlaying ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isPlaying ? 'Close' : 'Play'}
              </Button>
            </div>

            {recording.title ? (
              <p className="text-sm mt-2">{recording.title}</p>
            ) : null}

            <p className="text-xs text-muted-foreground mt-1.5">
              {new Date(recording.startedAt).toLocaleString()}
              {recording.endedAt ? (
                <> — {new Date(recording.endedAt).toLocaleString()}</>
              ) : null}
              {recording.openedBy ? <> · Opened by {recording.openedBy}</> : null}
            </p>

            {isPlaying ? (
              <div className="mt-3">
                <SessionRecordingPlayer
                  deviceId={deviceId}
                  recordingId={recording.id}
                  durationSeconds={recording.durationSeconds}
                />
              </div>
            ) : null}
          </div>
        );
      })}

      {totalPages > 1 ? (
        <nav
          className="flex items-center justify-between pt-1"
          aria-label="Pagination"
        >
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => { goToPage(Math.max(1, page - 1)); }}
          >
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {String(page)} of {String(totalPages)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => { goToPage(Math.min(totalPages, page + 1)); }}
          >
            Next
          </Button>
        </nav>
      ) : null}
    </div>
  );
}
